import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface PopoverFieldProps {
    /** Text shown inside the trigger button (summary of the current value). */
    summary: string;
    children: (close: () => void) => ReactNode;
    disabled?: boolean;
    error?: boolean;
    muted?: boolean;
    className?: string;
    panelWidth?: number;
}

/**
 * Dropdown-button trigger + portal popover panel, matching the design's
 * States / Tags / Licence-types trigger chrome. Portals to document.body so
 * it is never clipped by a modal's `overflow-hidden` dialog surface.
 */
export default function PopoverField({
    summary,
    children,
    disabled = false,
    error = false,
    muted = false,
    className = '',
    panelWidth = 320,
}: PopoverFieldProps) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

    const close = () => setOpen(false);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            const target = e.target as Node;
            if (rootRef.current?.contains(target)) return;
            if (panelRef.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    useLayoutEffect(() => {
        if (!open) {
            setPos(null);
            return;
        }
        const update = () => {
            const trigger = triggerRef.current;
            if (!trigger) return;
            const rect = trigger.getBoundingClientRect();
            const width = Math.max(rect.width, panelWidth);
            const gap = 6;
            const panelHeight = panelRef.current?.offsetHeight ?? 260;
            const spaceBelow = window.innerHeight - rect.bottom - gap;
            const placeUp = spaceBelow < panelHeight && rect.top > spaceBelow;
            const top = placeUp ? rect.top - gap - panelHeight : rect.bottom + gap;
            const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
            setPos({ top, left, width });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open, panelWidth]);

    useEffect(() => {
        if (disabled && open) setOpen(false);
    }, [disabled, open]);

    return (
        <div ref={rootRef} className={['relative', className].join(' ')}>
            <button
                ref={triggerRef}
                type="button"
                disabled={disabled}
                onClick={() => setOpen((v) => !v)}
                className="w-full h-[40px] flex items-center justify-between gap-2.5 rounded-[10px] border-[1.5px] bg-white px-3 text-left transition-colors duration-150"
                style={{
                    borderColor: error
                        ? 'var(--cpm-shortfall-border)'
                        : open
                          ? 'var(--cpm-brand-purple)'
                          : 'var(--cpm-border-strong)',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.6 : 1,
                }}
            >
                <span
                    className="min-w-0 flex-1 truncate text-[13px] font-bold"
                    style={{
                        color: muted ? 'var(--cpm-text-secondary)' : 'var(--cpm-text-primary)',
                    }}
                >
                    {summary}
                </span>
                <span className="flex-none text-[9px] text-[var(--cpm-brand-purple)]">▼</span>
            </button>
            {open && typeof document !== 'undefined'
                ? createPortal(
                      <div
                          ref={panelRef}
                          className="cpm-theme cpm-animate-pop-in flex flex-col bg-white"
                          style={{
                              position: 'fixed',
                              top: pos?.top ?? -9999,
                              left: pos?.left ?? -9999,
                              width: pos?.width ?? panelWidth,
                              zIndex: 90,
                              visibility: pos ? 'visible' : 'hidden',
                              padding: 11,
                              borderRadius: 'var(--cpm-radius-xl)',
                              border: '1px solid var(--cpm-border-default)',
                              boxShadow: 'var(--cpm-shadow-dropdown)',
                          }}
                      >
                          {children(close)}
                      </div>,
                      document.body
                  )
                : null}
        </div>
    );
}
